// IndexedDB database initialization and helpers
import { openDB, deleteDB, type IDBPDatabase } from "idb";
import { STORAGE_KEYS } from "@/utils/storageKeys";
import {
  DB_NAME,
  DB_VERSION,
  STORES,
  type Manuscript,
  type Series,
  type Collection,
  type PlannerStructure,
  type CodexEntry,
  type Goals,
  type FormatSettings,
  type SceneContent,
  type OutboxEntry,
} from "@/types/database";
import { logger } from "@/lib/logger";
import { supabase } from "@/integrations/supabase/client";
import { isWebCompanionPlatform } from "@/utils/platform";

interface DeckleDB {
  manuscripts: {
    key: string;
    value: Manuscript;
    indexes: {
      type: string;
      seriesId: string;
      collectionId: string;
      lastModified: Date;
    };
  };
  series: {
    key: string;
    value: Series;
  };
  collections: {
    key: string;
    value: Collection;
  };
  plannerStructures: {
    key: string;
    value: PlannerStructure;
  };
  codexEntries: {
    key: string;
    value: CodexEntry;
    indexes: {
      manuscriptId: string;
      section: string;
    };
  };
  goals: {
    key: string;
    value: Goals;
  };
  formatSettings: {
    key: string;
    value: FormatSettings;
  };
  encryptionSettings: {
    key: string;
    value: {
      id: string;
      key?: string;
    };
  };
  sceneContent: {
    key: string;
    value: SceneContent;
    indexes: {
      manuscriptId: string;
    };
  };
  syncOutbox: {
    key: string;
    value: OutboxEntry;
  };
}

let dbInstance: IDBPDatabase<DeckleDB> | null = null;
let dbPromise: Promise<IDBPDatabase<DeckleDB>> | null = null;
let currentDbName: string | null = null;
let currentUserId: string | null = null;

export async function initDB(dbName?: string): Promise<IDBPDatabase<DeckleDB>> {
  let resolvedName = dbName;

  if (!resolvedName) {
    resolvedName = DB_NAME;

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const userId = session?.user?.id ?? null;

      if (userId) {
        resolvedName = `deckle-db-${userId}`;
      }
    } catch (err) {
      console.error(
        "[DB] Failed to get session for database initialization:",
        err
      );
    }
  }

  if (dbInstance && currentDbName !== resolvedName) {
    logger.log(
      `[DB] initDB requested a different database name (${resolvedName}) than cached (${currentDbName}). Closing cache.`
    );

    try {
      dbInstance.close();
    } catch {
      // Ignore an already-closed connection.
    }

    dbInstance = null;
    dbPromise = null;
  }

  currentDbName = resolvedName;

  if (dbInstance) {
    if (dbInstance.version !== DB_VERSION) {
      dbInstance.close();
      dbInstance = null;
    } else {
      return dbInstance;
    }
  }

  dbInstance = await openDB<DeckleDB>(resolvedName, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORES.MANUSCRIPTS)) {
        const manuscriptsStore = db.createObjectStore(STORES.MANUSCRIPTS, {
          keyPath: "id",
        });
        manuscriptsStore.createIndex("type", "type");
        manuscriptsStore.createIndex("seriesId", "seriesId");
        manuscriptsStore.createIndex("collectionId", "collectionId");
        manuscriptsStore.createIndex("lastModified", "lastModified");
      }

      if (!db.objectStoreNames.contains(STORES.SERIES)) {
        db.createObjectStore(STORES.SERIES, { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains(STORES.COLLECTIONS)) {
        db.createObjectStore(STORES.COLLECTIONS, { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains(STORES.PLANNER_STRUCTURES)) {
        db.createObjectStore(STORES.PLANNER_STRUCTURES, { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains(STORES.CODEX_ENTRIES)) {
        const codexStore = db.createObjectStore(STORES.CODEX_ENTRIES, {
          keyPath: "id",
        });
        codexStore.createIndex("manuscriptId", "manuscriptId");
        codexStore.createIndex("section", "section");
      }

      if (!db.objectStoreNames.contains(STORES.GOALS)) {
        db.createObjectStore(STORES.GOALS, { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains(STORES.FORMAT_SETTINGS)) {
        db.createObjectStore(STORES.FORMAT_SETTINGS, { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains(STORES.ENCRYPTION_SETTINGS)) {
        db.createObjectStore(STORES.ENCRYPTION_SETTINGS, { keyPath: "id" });
      }

      if (!db.objectStoreNames.contains(STORES.SCENE_CONTENT)) {
        const sceneContentStore = db.createObjectStore(
          STORES.SCENE_CONTENT,
          { keyPath: "id" }
        );
        sceneContentStore.createIndex("manuscriptId", "manuscriptId");
      }

      if (!db.objectStoreNames.contains(STORES.SYNC_OUTBOX)) {
        db.createObjectStore(STORES.SYNC_OUTBOX, { keyPath: "id" });
      }
    },
  });

  return dbInstance;
}

async function migrateLegacyDatabase(
  userId: string,
  targetDb: IDBPDatabase<DeckleDB>
): Promise<void> {
  const migrationFlag = `deckle_db_migrated_${userId}`;

  if (localStorage.getItem(migrationFlag) === "true") {
    return;
  }

  try {
    let legacyExists = true;

    if (window.indexedDB.databases) {
      const dbList = await window.indexedDB.databases();
      legacyExists = dbList.some((database) => database.name === DB_NAME);
    }

    if (!legacyExists) {
      localStorage.setItem(migrationFlag, "true");
      return;
    }

    const legacyDb = await openDB(DB_NAME).catch(() => null);

    if (!legacyDb) {
      localStorage.setItem(migrationFlag, "true");
      return;
    }

    const manuscriptsCount = await legacyDb
      .count(STORES.MANUSCRIPTS)
      .catch(() => 0);

    if (manuscriptsCount === 0) {
      legacyDb.close();
      localStorage.setItem(migrationFlag, "true");
      return;
    }

    logger.log(
      `[DB Migration] Legacy data found. Migrating to ${targetDb.name}.`
    );

    const storesToMigrate = [
      STORES.MANUSCRIPTS,
      STORES.SERIES,
      STORES.COLLECTIONS,
      STORES.PLANNER_STRUCTURES,
      STORES.CODEX_ENTRIES,
      STORES.GOALS,
      STORES.FORMAT_SETTINGS,
      STORES.SCENE_CONTENT,
    ] as const;

    for (const storeName of storesToMigrate) {
      if (
        legacyDb.objectStoreNames.contains(storeName) &&
        targetDb.objectStoreNames.contains(storeName)
      ) {
        const items = await legacyDb.getAll(storeName);

        if (items.length > 0) {
          const transaction = targetDb.transaction(storeName, "readwrite");

          for (const item of items) {
            await transaction.store.put(item);
          }

          await transaction.done;

          logger.log(
            `[DB Migration] Migrated ${items.length} items for store ${storeName}.`
          );
        }
      }
    }

    legacyDb.close();
    await deleteDB(DB_NAME);

    localStorage.setItem(migrationFlag, "true");
    logger.log(
      `[DB Migration] Successfully migrated legacy data to ${targetDb.name} and deleted the legacy database.`
    );
  } catch (err) {
    console.error("[DB Migration] Failed to migrate legacy database:", err);
  }
}

supabase.auth.onAuthStateChange((_event, session) => {
  const newUserId = session?.user?.id ?? null;

  if (newUserId !== currentUserId) {
    logger.log(
      `[DB] Auth state changed from ${currentUserId} to ${newUserId}. Resetting the database connection.`
    );

    if (dbInstance) {
      try {
        dbInstance.close();
      } catch {
        // Ignore an already-closed connection.
      }

      dbInstance = null;
    }

    dbPromise = null;
    currentDbName = null;
    currentUserId = newUserId;
  }
});

export async function getDB(): Promise<IDBPDatabase<DeckleDB>> {
  if (isWebCompanionPlatform()) {
    logger.log(
      "[DB] getDB() called on the Web Companion platform. Accessing IndexedDB storage."
    );
  }

  let dbName = DB_NAME;
  let userId: string | null = null;

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    userId = session?.user?.id ?? null;

    if (userId) {
      dbName = `deckle-db-${userId}`;
    }
  } catch (err) {
    console.error("[DB] Failed to get session for database naming:", err);
  }

  if (dbInstance && currentDbName !== dbName) {
    logger.log(
      `[DB] Database name changed from ${currentDbName} to ${dbName}. Closing the old connection.`
    );

    try {
      dbInstance.close();
    } catch {
      // Ignore an already-closed connection.
    }

    dbInstance = null;
    dbPromise = null;
  }

  if (!dbPromise || currentDbName !== dbName) {
    currentDbName = dbName;
    currentUserId = userId;

    dbPromise = initDB(dbName)
      .then(async (database) => {
        if (
          userId &&
          localStorage.getItem(`deckle_db_migrated_${userId}`) !== "true"
        ) {
          await migrateLegacyDatabase(userId, database);
        }

        return database;
      })
      .catch((err) => {
        dbPromise = null;
        currentDbName = null;
        throw err;
      });
  }

  return dbPromise;
}

export async function isDatabaseEmpty(): Promise<boolean> {
  const db = await getDB();
  const count = await db.count(STORES.MANUSCRIPTS);
  return count === 0;
}

export async function getAllManuscripts(): Promise<Manuscript[]> {
  const db = await getDB();
  return db.getAll(STORES.MANUSCRIPTS);
}

export async function getManuscript(
  id: string
): Promise<Manuscript | undefined> {
  const db = await getDB();
  return db.get(STORES.MANUSCRIPTS, id);
}

function getLocalDateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}-${String(date.getDate()).padStart(2, "0")}`;
}

function mergeGoalsDailyProgress(
  goals: Goals | undefined,
  manuscriptId: string,
  currentWordCount: number,
  previousWordCount: number
): Goals {
  const today = getLocalDateString(new Date());

  if (!goals) {
    const targetDate = new Date();
    targetDate.setMonth(targetDate.getMonth() + 3);

    return {
      id: manuscriptId,
      totalGoal: 80000,
      wordsExcluded: 0,
      targetDate: getLocalDateString(targetDate),
      weekPattern: {
        sunday: "rest",
        monday: "write",
        tuesday: "write",
        wednesday: "write",
        thursday: "write",
        friday: "write",
        saturday: "rest",
      },
      dailyProgress: {
        [today]: {
          start: previousWordCount,
          latest: currentWordCount,
        },
      },
    };
  }

  const dailyProgress = { ...(goals.dailyProgress || {}) };
  const existingProgress = dailyProgress[today];

  dailyProgress[today] = existingProgress
    ? {
        ...existingProgress,
        latest: currentWordCount,
      }
    : {
        start: previousWordCount,
        latest: currentWordCount,
      };

  return {
    ...goals,
    dailyProgress,
  };
}

async function updateGoalsDailyProgress(
  manuscriptId: string,
  currentWordCount: number,
  previousWordCount: number
): Promise<void> {
  const goals = await getGoals(manuscriptId);
  await putGoals(
    mergeGoalsDailyProgress(
      goals,
      manuscriptId,
      currentWordCount,
      previousWordCount
    )
  );
}

export async function putManuscript(
  manuscript: Manuscript
): Promise<void> {
  const db = await getDB();
  const current = await db.get(STORES.MANUSCRIPTS, manuscript.id);
  const previousWordCount = current?.wordCount ?? 0;

  await db.put(STORES.MANUSCRIPTS, manuscript);

  if (manuscript.wordCount !== undefined) {
    try {
      await updateGoalsDailyProgress(
        manuscript.id,
        manuscript.wordCount,
        previousWordCount
      );
    } catch (err) {
      console.error("[DB] Failed to update goals daily progress:", err);
    }
  }
}

export async function deleteManuscript(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.MANUSCRIPTS, id);
}

export async function deleteManuscriptCascade(
  id: string
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [
      STORES.MANUSCRIPTS,
      STORES.PLANNER_STRUCTURES,
      STORES.GOALS,
      STORES.FORMAT_SETTINGS,
      STORES.SCENE_CONTENT,
      STORES.CODEX_ENTRIES,
    ],
    "readwrite"
  );

  const sceneStore = transaction.objectStore(STORES.SCENE_CONTENT);
  const codexStore = transaction.objectStore(STORES.CODEX_ENTRIES);

  const [sceneKeys, codexKeys] = await Promise.all([
    sceneStore.index("manuscriptId").getAllKeys(id),
    codexStore.index("manuscriptId").getAllKeys(id),
  ]);

  await Promise.all([
    transaction.objectStore(STORES.MANUSCRIPTS).delete(id),
    transaction.objectStore(STORES.PLANNER_STRUCTURES).delete(id),
    transaction.objectStore(STORES.GOALS).delete(id),
    transaction.objectStore(STORES.FORMAT_SETTINGS).delete(id),
    ...sceneKeys.map((key) => sceneStore.delete(key)),
    ...codexKeys.map((key) => codexStore.delete(key)),
  ]);

  await transaction.done;
}

export async function deleteManuscriptCascadeWithOutbox(
  id: string,
  outboxEntry: OutboxEntry
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [
      STORES.MANUSCRIPTS,
      STORES.PLANNER_STRUCTURES,
      STORES.GOALS,
      STORES.FORMAT_SETTINGS,
      STORES.SCENE_CONTENT,
      STORES.CODEX_ENTRIES,
      STORES.SYNC_OUTBOX,
    ],
    "readwrite"
  );

  const sceneStore = transaction.objectStore(STORES.SCENE_CONTENT);
  const codexStore = transaction.objectStore(STORES.CODEX_ENTRIES);

  const [sceneKeys, codexKeys] = await Promise.all([
    sceneStore.index("manuscriptId").getAllKeys(id),
    codexStore.index("manuscriptId").getAllKeys(id),
  ]);

  await Promise.all([
    transaction.objectStore(STORES.MANUSCRIPTS).delete(id),
    transaction.objectStore(STORES.PLANNER_STRUCTURES).delete(id),
    transaction.objectStore(STORES.GOALS).delete(id),
    transaction.objectStore(STORES.FORMAT_SETTINGS).delete(id),
    ...sceneKeys.map((key) => sceneStore.delete(key)),
    ...codexKeys.map((key) => codexStore.delete(key)),
    transaction.objectStore(STORES.SYNC_OUTBOX).put(outboxEntry),
  ]);

  await transaction.done;
}

export async function getAllSeries(): Promise<Series[]> {
  const db = await getDB();
  return db.getAll(STORES.SERIES);
}

export async function getSeries(id: string): Promise<Series | undefined> {
  const db = await getDB();
  return db.get(STORES.SERIES, id);
}

export async function putSeries(series: Series): Promise<void> {
  const db = await getDB();
  await db.put(STORES.SERIES, series);
}

export async function deleteSeries(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.SERIES, id);
}

export async function getAllCollections(): Promise<Collection[]> {
  const db = await getDB();
  return db.getAll(STORES.COLLECTIONS);
}

export async function getCollection(
  id: string
): Promise<Collection | undefined> {
  const db = await getDB();
  return db.get(STORES.COLLECTIONS, id);
}

export async function putCollection(
  collection: Collection
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.COLLECTIONS, collection);
}

export async function deleteCollection(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.COLLECTIONS, id);
}

export async function getPlannerStructure(
  manuscriptId: string
): Promise<PlannerStructure | undefined> {
  const db = await getDB();
  return db.get(STORES.PLANNER_STRUCTURES, manuscriptId);
}

export async function putPlannerStructure(
  structure: PlannerStructure
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.PLANNER_STRUCTURES, structure);
}

export async function getCodexEntriesForManuscript(
  manuscriptId: string
): Promise<CodexEntry[]> {
  const db = await getDB();
  const index = db
    .transaction(STORES.CODEX_ENTRIES)
    .store.index("manuscriptId");
  return index.getAll(manuscriptId);
}

export async function getCodexEntry(
  id: string
): Promise<CodexEntry | undefined> {
  const db = await getDB();
  return db.get(STORES.CODEX_ENTRIES, id);
}

export async function putCodexEntry(entry: CodexEntry): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(STORES.CODEX_ENTRIES, "readwrite");
  const existing = await transaction.store.get(entry.id);
  const merged = existing ? { ...existing, ...entry } : entry;

  await transaction.store.put(merged);
  await transaction.done;
}

export async function deleteCodexEntry(id: string): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.CODEX_ENTRIES, id);
}

export async function getGoals(
  manuscriptId: string
): Promise<Goals | undefined> {
  const db = await getDB();
  return db.get(STORES.GOALS, manuscriptId);
}

export async function putGoals(goals: Goals): Promise<void> {
  const db = await getDB();
  await db.put(STORES.GOALS, goals);
}

export async function getFormatSettings(
  manuscriptId: string
): Promise<FormatSettings | undefined> {
  const db = await getDB();
  return db.get(STORES.FORMAT_SETTINGS, manuscriptId);
}

export async function putFormatSettings(
  settings: FormatSettings
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.FORMAT_SETTINGS, settings);
}

export async function getMasterKeyLocal(): Promise<string | undefined> {
  const db = await getDB();
  const record = await db.get(STORES.ENCRYPTION_SETTINGS, "masterKey");
  return record?.key;
}

export async function putMasterKeyLocal(
  keyBase64: string
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.ENCRYPTION_SETTINGS, {
    id: "masterKey",
    key: keyBase64,
  });
}

export async function clearMasterKeyLocal(): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.ENCRYPTION_SETTINGS, "masterKey");
}

export function clearSyncMetadata(): void {
  try {
    const keysToRemove: string[] = [];

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);

      if (
        key &&
        (key.startsWith(STORAGE_KEYS.LAST_SYNCED_PREFIX) ||
          key.startsWith(STORAGE_KEYS.E2E_SETUP_PREFIX))
      ) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (err) {
    console.error("[DB] Failed to clear sync metadata:", err);
  }
}

export async function getSceneContent(
  sceneId: string
): Promise<SceneContent | undefined> {
  const db = await getDB();
  return db.get(STORES.SCENE_CONTENT, sceneId);
}

export async function putSceneContent(
  entry: SceneContent
): Promise<void> {
  try {
    const db = await getDB();
    await db.put(STORES.SCENE_CONTENT, entry);
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);

    if (message.includes("not found") || message.includes("closed")) {
      console.warn(
        "[DB] putSceneContent failed, reinitializing:",
        message
      );
      dbInstance = null;
      dbPromise = null;

      const db = await getDB();
      await db.put(STORES.SCENE_CONTENT, entry);
    } else {
      throw err;
    }
  }
}

export async function deleteSceneContent(
  sceneId: string
): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.SCENE_CONTENT, sceneId);
}

export async function getAllSceneContentForManuscript(
  manuscriptId: string
): Promise<SceneContent[]> {
  const db = await getDB();
  const index = db
    .transaction(STORES.SCENE_CONTENT)
    .store.index("manuscriptId");
  return index.getAll(manuscriptId);
}

export async function deleteAllSceneContentForManuscript(
  manuscriptId: string
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    STORES.SCENE_CONTENT,
    "readwrite"
  );
  const index = transaction.store.index("manuscriptId");
  const keys = await index.getAllKeys(manuscriptId);

  for (const key of keys) {
    await transaction.store.delete(key);
  }

  await transaction.done;
}

export async function deleteAllCodexEntriesForManuscript(
  manuscriptId: string
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    STORES.CODEX_ENTRIES,
    "readwrite"
  );
  const index = transaction.store.index("manuscriptId");
  const keys = await index.getAllKeys(manuscriptId);

  for (const key of keys) {
    await transaction.store.delete(key);
  }

  await transaction.done;
}

export async function putOutboxEntry(
  entry: OutboxEntry
): Promise<void> {
  const db = await getDB();
  await db.put(STORES.SYNC_OUTBOX, entry);
}

export async function deleteOutboxEntry(
  manuscriptId: string
): Promise<void> {
  const db = await getDB();
  await db.delete(STORES.SYNC_OUTBOX, manuscriptId);
}

export async function getOutboxEntry(
  manuscriptId: string
): Promise<OutboxEntry | undefined> {
  const db = await getDB();
  return db.get(STORES.SYNC_OUTBOX, manuscriptId);
}

export async function getAllOutboxEntries(): Promise<OutboxEntry[]> {
  const db = await getDB();
  return db.getAll(STORES.SYNC_OUTBOX);
}

export async function putManuscriptWithOutbox(
  manuscript: Manuscript,
  outboxEntry: OutboxEntry
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.MANUSCRIPTS, STORES.GOALS, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  const manuscriptStore = transaction.objectStore(STORES.MANUSCRIPTS);
  const goalsStore = transaction.objectStore(STORES.GOALS);

  const [current, existingGoals] = await Promise.all([
    manuscriptStore.get(manuscript.id),
    goalsStore.get(manuscript.id),
  ]);

  const writes = [
    manuscriptStore.put(manuscript),
    transaction.objectStore(STORES.SYNC_OUTBOX).put(outboxEntry),
  ];

  if (manuscript.wordCount !== undefined) {
    writes.push(
      goalsStore.put(
        mergeGoalsDailyProgress(
          existingGoals,
          manuscript.id,
          manuscript.wordCount,
          current?.wordCount ?? 0
        )
      )
    );
  }

  await Promise.all(writes);
  await transaction.done;
}

export async function putPlannerStructureWithOutbox(
  structure: PlannerStructure,
  outboxEntry: OutboxEntry
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.PLANNER_STRUCTURES, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  await Promise.all([
    transaction
      .objectStore(STORES.PLANNER_STRUCTURES)
      .put(structure),
    transaction.objectStore(STORES.SYNC_OUTBOX).put(outboxEntry),
  ]);

  await transaction.done;
}

export async function putSceneContentWithOutbox(
  entry: SceneContent,
  outboxEntry: OutboxEntry
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.SCENE_CONTENT, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  await Promise.all([
    transaction.objectStore(STORES.SCENE_CONTENT).put(entry),
    transaction.objectStore(STORES.SYNC_OUTBOX).put(outboxEntry),
  ]);

  await transaction.done;
}

export async function deleteSceneContentWithOutbox(
  sceneId: string,
  outboxEntry: OutboxEntry
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.SCENE_CONTENT, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  await Promise.all([
    transaction.objectStore(STORES.SCENE_CONTENT).delete(sceneId),
    transaction.objectStore(STORES.SYNC_OUTBOX).put(outboxEntry),
  ]);

  await transaction.done;
}

export async function putCodexEntryWithOutbox(
  entry: CodexEntry,
  outboxEntry: OutboxEntry
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.CODEX_ENTRIES, STORES.SYNC_OUTBOX],
    "readwrite"
  );
  const codexStore = transaction.objectStore(STORES.CODEX_ENTRIES);
  const existing = await codexStore.get(entry.id);
  const merged = existing ? { ...existing, ...entry } : entry;

  await Promise.all([
    codexStore.put(merged),
    transaction.objectStore(STORES.SYNC_OUTBOX).put(outboxEntry),
  ]);

  await transaction.done;
}

export async function deleteCodexEntryWithOutbox(
  entryId: string,
  outboxEntry: OutboxEntry
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.CODEX_ENTRIES, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  await Promise.all([
    transaction.objectStore(STORES.CODEX_ENTRIES).delete(entryId),
    transaction.objectStore(STORES.SYNC_OUTBOX).put(outboxEntry),
  ]);

  await transaction.done;
}

export async function putGoalsWithOutbox(
  goals: Goals,
  outboxEntry: OutboxEntry
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.GOALS, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  await Promise.all([
    transaction.objectStore(STORES.GOALS).put(goals),
    transaction.objectStore(STORES.SYNC_OUTBOX).put(outboxEntry),
  ]);

  await transaction.done;
}

export async function putFormatSettingsWithOutbox(
  settings: FormatSettings,
  outboxEntry: OutboxEntry
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.FORMAT_SETTINGS, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  await Promise.all([
    transaction.objectStore(STORES.FORMAT_SETTINGS).put(settings),
    transaction.objectStore(STORES.SYNC_OUTBOX).put(outboxEntry),
  ]);

  await transaction.done;
}

export async function putSeriesWithOutbox(
  series: Series,
  outboxEntries: OutboxEntry[]
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.SERIES, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  await Promise.all([
    transaction.objectStore(STORES.SERIES).put(series),
    ...outboxEntries.map((entry) =>
      transaction.objectStore(STORES.SYNC_OUTBOX).put(entry)
    ),
  ]);

  await transaction.done;
}

export async function deleteSeriesWithOutbox(
  seriesId: string,
  outboxEntries: OutboxEntry[]
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.SERIES, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  await Promise.all([
    transaction.objectStore(STORES.SERIES).delete(seriesId),
    ...outboxEntries.map((entry) =>
      transaction.objectStore(STORES.SYNC_OUTBOX).put(entry)
    ),
  ]);

  await transaction.done;
}

export async function putCollectionWithOutbox(
  collection: Collection,
  outboxEntries: OutboxEntry[]
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.COLLECTIONS, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  await Promise.all([
    transaction.objectStore(STORES.COLLECTIONS).put(collection),
    ...outboxEntries.map((entry) =>
      transaction.objectStore(STORES.SYNC_OUTBOX).put(entry)
    ),
  ]);

  await transaction.done;
}

export async function deleteCollectionWithOutbox(
  collectionId: string,
  outboxEntries: OutboxEntry[]
): Promise<void> {
  const db = await getDB();
  const transaction = db.transaction(
    [STORES.COLLECTIONS, STORES.SYNC_OUTBOX],
    "readwrite"
  );

  await Promise.all([
    transaction.objectStore(STORES.COLLECTIONS).delete(collectionId),
    ...outboxEntries.map((entry) =>
      transaction.objectStore(STORES.SYNC_OUTBOX).put(entry)
    ),
  ]);

  await transaction.done;
}

/**
 * Wipes all local IndexedDB databases and associated local/session storage metadata for the user.
 */
export async function clearAllLocalUserData(userId?: string | null): Promise<void> {
  try {
    if (dbInstance) {
      dbInstance.close();
      dbInstance = null;
    }
    dbPromise = null;
    currentDbName = null;
    currentUserId = null;

    // Delete both the default anonymous db and the user-scoped db if indexedDB is supported
    if (typeof indexedDB !== "undefined") {
      await deleteDB(DB_NAME).catch((err) => console.warn("[DB] Failed to delete default DB:", err));
      if (userId) {
        await deleteDB(`deckle-db-${userId}`).catch((err) =>
          console.warn(`[DB] Failed to delete user DB for ${userId}:`, err)
        );
      }
    }

    clearSyncMetadata();

    const { clearMasterKeyForUser } = await import("@/lib/cryptoKeyStore");
    await clearMasterKeyForUser(userId || undefined).catch((err) =>
      console.warn("[DB] Failed to clear master key:", err)
    );

    // Clear all deckle / birch localStorage entries
    if (typeof localStorage !== "undefined") {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith("deckle") || key.startsWith("birch"))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((k) => localStorage.removeItem(k));
    }

    logger.log("[DB] Successfully cleared all local user data.");
  } catch (err) {
    console.error("[DB] Error clearing local user data:", err);
    throw err;
  }
}
